import {IconType} from 'components/Icon/types';
import Notification, {NotificationType} from 'components/Notification';
import {FC} from 'react';
import {t} from 'ttag';

export type MessageKind =
    | 'cookie_not_set'
    | 'expired_link_token'
    | 'expired_onetime_link_token'
    | 'expired_session_token'
    | 'invalid_link_token'
    | 'invalid_session_token'
    | 'logged_out'
    | 'missing_link_token'
    | 'missing_session_token'
    | 'portal_not_available'
    | 'unknown_error';

export const allMessageKinds: MessageKind[] = [
    'cookie_not_set',
    'logged_out',
    'expired_link_token',
    'expired_onetime_link_token',
    'expired_session_token',
    'invalid_link_token',
    'missing_link_token',
    'invalid_session_token',
    'missing_session_token',
    'unknown_error',
];

export const messageKey: Record<MessageKind, string> = {
    cookie_not_set: 'cookieNotSet',
    expired_link_token: 'badLinkToken',
    expired_onetime_link_token: 'badLinkToken',
    expired_session_token: 'expiredSessionToken',
    invalid_link_token: 'badLinkToken',
    invalid_session_token: 'invalidSessionToken',
    logged_out: 'loggedOut',
    missing_link_token: 'badLinkToken',
    missing_session_token: 'missingSessionToken',
    portal_not_available: 'portalNotAvailable',
    unknown_error: 'invalidSessionToken',
};

const infoType: [NotificationType, IconType] = ['system', 'info'];
const alertType: [NotificationType, IconType] = ['alert', 'warning'];
const dangerType: [NotificationType, IconType] = ['danger', 'danger'];

export const messageType: Record<MessageKind, [NotificationType, IconType]> = {
    cookie_not_set: alertType,
    expired_link_token: alertType,
    expired_onetime_link_token: alertType,
    expired_session_token: alertType,
    invalid_link_token: alertType,
    invalid_session_token: alertType,
    logged_out: infoType,
    missing_link_token: alertType,
    missing_session_token: infoType,
    portal_not_available: dangerType,
    unknown_error: dangerType,
};

interface Props {
    className?: string;
    kind?: MessageKind;
}

const PortalNotification: FC<Props> = ({className, kind}) => {
    if (!kind || !messageKey[kind]) return null;

    const [type, icon] = messageType[kind];

    const notification = (() => {
        return {
            badLinkToken: t`The Login Link you used is expired or otherwise invalid. Please request another link below.`,
            cookieNotSet: t`The MakeLeaps Cookie was rejected. Please change the cookies settings and request a new login link below.`,
            expiredSessionToken: t`Your session has expired. To access your portal again, please request a new Login Link below.`,
            invalidSessionToken: t`We were not able to confirm your session details. Please request a new login link below.`,
            loggedOut: t`You have logged out. Thank you for using Business Portals.`,
            missingSessionToken: t`The page you are trying to access requires a login. Please request a Login Link below.`,
            portalNotAvailable: t`The link you have accessed either doesn't exist or isn't available for the logged-in email address. Please confirm the link's URL.`,
        }[messageKey[kind]];
    })();

    return (
        <Notification className={className} icon={icon} type={type}>
            {notification}
        </Notification>
    );
};

export default PortalNotification;
